// src/pages/Optometrist/OptometristDashboardApi.tsx
import React, { useEffect, useState } from "react";
import OptometristDashboardPage from "./OptometristDashboardPage";
import api from "../../api/axiosConfig";
import "../../center admin css/centerAdmin.css";

/* ---------------- Local types (separate from WelcomeScreen) ---------------- */

export interface UserProfile {
  _id: string;
  displayName: string;
  email: string;
  centerAssignments?: { centerId: string }[];
}

export interface IUserRef {
  userId: string;
  userName: string;
}
export interface IStationRef {
  stationId: string;
  stationName: string;
  users: IUserRef[];
}
export interface ICenterConfig {
  _id: string;
  appointmentClusterId: string;
  centerId: string;
  centerName: string;
  stations: IStationRef[];
}

/** IMPORTANT: keep this structurally identical to the page’s expectation */
export interface IScheduleDetails {
  slotDesigns: { [weekday: string]: string };
  totalSlots: number;
  screeningStartDate: string; // DD-MM-YYYY
  screeningEndDate: string; // DD-MM-YYYY
  totalScreeningDays: number; // we compute if backend omits it
}

export interface IScreeningCounts {
  totalBookingsToday: number;
  totalCompletedToday: number;
  totalPendingToday: number;
}

/* ---------------- Utils ---------------- */

function getCenterIdFromConfig(): string {
  try {
    const rawConfig = sessionStorage.getItem("configResponse");

    if (!rawConfig) return "";

    const parsedConfig = JSON.parse(rawConfig);

    return parsedConfig?.data?.[0]?.centerId || "";
  } catch (error) {
    console.error("Failed to read configResponse:", error);
    return "";
  }
}

function readCachedUser(): UserProfile | null {
  const keys = ["backendResponse", "CurrrentResponse", "CurrentResponse"];
  for (const k of keys) {
    try {
      const raw = sessionStorage.getItem(k);
      if (raw) return JSON.parse(raw) as UserProfile;
    } catch {}
  }
  return null;
}

async function waitFor(
  predicate: () => boolean,
  { attempts = 30, intervalMs = 100 } = {},
): Promise<boolean> {
  for (let i = 0; i < attempts; i++) {
    if (predicate()) return true;
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return predicate();
}

/* ---------------- Component ---------------- */

const OptometristDashboardApi: React.FC = () => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [centerConfig, setCenterConfig] = useState<ICenterConfig | null>(null);
  const [scheduleDetails, setScheduleDetails] =
    useState<IScheduleDetails | null>(null);
  const [screeningCounts, setScreeningCounts] =
    useState<IScreeningCounts | null>(null);
  // const [totalScreeningDays, setTotalScreeningDays] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let aborted = false;

    const run = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log("[Optometrist] waiting for cached user…");
        await waitFor(() => !!readCachedUser());
        console.log("[Optometrist] waiting for accessToken…");
        await waitFor(() => !!sessionStorage.getItem("accessToken"));
        if (aborted) return;

        const cached = readCachedUser();
        if (!cached) throw new Error("Cached user not found.");
        setUserProfile(cached);
        console.log("[Optometrist] cached user:", cached.displayName);

        const token = sessionStorage.getItem("accessToken");
        if (!token) throw new Error("No access token found.");
        const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

        // const centerId = cached.centerAssignments?.[0]?.centerId;
        // if (!centerId) throw new Error("No centerId on cached profile.");
        // console.log("[Optometrist] centerId:", centerId);

        // ---- Resolve centerId from configResponse ----
        const centerId = getCenterIdFromConfig();

        if (!centerId) {
          throw new Error("No centerId found in configResponse.");
        }

        console.log("[Optometrist] centerId:", centerId);

        // 1) Center config
        const centerCfgUrl = `/api/v1/center/${centerId}/config`;
        console.log("[Optometrist] GET", centerCfgUrl);
        // const { data: cfgResp } = await api.center_api.get<{
        //   data: ICenterConfig;
        // }>(centerCfgUrl, authHeaders);
        // const cfg = cfgResp?.data;
        // if (!cfg?.centerId) throw new Error("Center config missing centerId.");
        // setCenterConfig(cfg);
        // console.log("[Optometrist] center config OK:", cfg.centerName);

        const centerRes = await api.center_api.get<{
          data: ICenterConfig | ICenterConfig[];
        }>(centerCfgUrl, authHeaders);

        const rawCenterConfig = centerRes.data?.data;

        const cfg = Array.isArray(rawCenterConfig)
          ? rawCenterConfig[0]
          : rawCenterConfig;

        console.log("[Optometrist] Center config response:", centerRes.data);
        console.log("[Optometrist] Extracted center config:", cfg);

        if (!cfg?.centerId) {
          throw new Error("Center config missing centerId.");
        }

        setCenterConfig(cfg);
        console.log("[Optometrist] center config OK:", cfg.centerName);

        // 2) Schedule details
        const scheduleUrl = `/api/appointments/screening/details/${cfg.centerId}`;
        console.log("[Optometrist] GET", scheduleUrl);
        const scheduleRes = await api.appointment_api.get<IScheduleDetails>(
          scheduleUrl,
          authHeaders,
        );
        setScheduleDetails(scheduleRes.data);
        console.log("[Optometrist] schedule details OK");

        // 3) Screening counts
        const countsUrl = `/api/appointments/screening/counts/${cfg.centerId}`;
        console.log("[Optometrist] GET", countsUrl);
        const { data: counts } =
          await api.appointment_api.get<IScreeningCounts>(
            countsUrl,
            authHeaders,
          );
        setScreeningCounts(counts);
        console.log("[Optometrist] screening counts OK");
      } catch (e: any) {
        console.error("OptometristDashboardApi error:", e);
        setError("Could not load data.");
      } finally {
        if (!aborted) setLoading(false);
      }
    };

    run();

    const onStorage = (e: StorageEvent) => {
      if (
        e.storageArea === sessionStorage &&
        [
          "backendResponse",
          "CurrrentResponse",
          "CurrentResponse",
          "accessToken",
        ].includes(e.key || "")
      ) {
        run();
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      aborted = true;
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const displayName = userProfile
    ? userProfile.displayName
    : loading
      ? "..."
      : "Guest";

  return (
    <OptometristDashboardPage
      userName={displayName}
      isLoading={loading}
      error={error}
      centerConfig={centerConfig}
      scheduleDetails={scheduleDetails}
      screeningCounts={screeningCounts}
    />
  );
};

export default OptometristDashboardApi;
